import { useEffect, useState } from 'react';
import { History } from 'lucide-react';
import { describeChangeAuthor, getChangeHistory } from '../../db/audit-store';
import { AUDIT_ACTION_LABELS } from '../../types/audit';
import type { AuditEntityType, AuditLogEntry } from '../../types/audit';

interface ChangeHistoryProps {
  entityType: AuditEntityType;
  entityId: string;
  /** How many rows the card shows before it stops. */
  limit?: number;
  /** What the section says when the record has never been changed. */
  emptyText?: string;
  /** Re-reads when this changes — the record was written while the card was open. */
  revision?: unknown;
}

/**
 * "היסטוריית שינויים" — who changed what on this record, newest first.
 *
 * Every line is read off a frozen ledger row: the name and the job are the ones
 * the person held when they acted, never the ones the directory holds now. That
 * is the difference between a history and a report.
 *
 * The section reads the ledger itself rather than being handed rows, because it
 * is dropped into two different modals that have no reason to know the audit
 * store exists.
 */
export function ChangeHistory({
  entityType,
  entityId,
  limit = 8,
  emptyText = 'טרם בוצעו שינויים ברשומה זו.',
  revision,
}: ChangeHistoryProps) {
  const [entries, setEntries] = useState<readonly AuditLogEntry[]>([]);

  useEffect(() => {
    let live = true;
    void getChangeHistory(entityType, entityId, limit).then((rows) => {
      if (live) setEntries(rows);
    });
    return () => {
      live = false;
    };
  }, [entityType, entityId, limit, revision]);

  return (
    <section className="rounded-2xl border border-line p-4" data-change-history={entityType}>
      <h3 className="font-semibold text-sm flex items-center gap-2 mb-2">
        <History size={16} className="text-brand-500" aria-hidden />
        היסטוריית שינויים ({entries.length})
      </h3>
      {entries.length === 0 ? (
        <p className="text-sm text-ink/50">{emptyText}</p>
      ) : (
        <ul className="space-y-1.5">
          {entries.map((entry) => (
            <li key={entry.id} className="rounded-xl border border-line px-3 py-2">
              <p className="text-sm font-medium">{AUDIT_ACTION_LABELS[entry.action]}</p>
              {entry.changes?.length ? (
                <ul className="mt-1 space-y-0.5">
                  {entry.changes.map((change) => (
                    <li key={change.field} className="text-xs text-ink/70">
                      <span className="text-ink/50">{change.label}:</span> {change.from}{' '}
                      <span aria-hidden>←</span> <span className="font-medium">{change.to}</span>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="mt-0.5 text-xs text-ink/60">{entry.summary}</p>
              )}
              <p className="mt-1 text-xs text-ink/50">{describeChangeAuthor(entry)}</p>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
